import QRCode from 'qrcode';
import { api } from '../api.js';
import { auth } from '../auth.js';
import { renderNavigation } from '../components/navigation.js';
import { escapeHtml, showToast, formatDate, enhanceCodeInput } from '../utils.js';

/** Profile, password change, and self-service TOTP enrollment. */

const app = () => document.getElementById('app');

export async function renderProfile() {
    const user = auth.getUser();

    app().innerHTML = `
        <div class="container profile-container">
            <div class="section-header"><h1>Your account</h1></div>

            <section class="form-section">
                <h2>Details</h2>
                <form id="account-details-form" class="account-form" novalidate>
                    <div class="form-group">
                        <label for="full-name">Name</label>
                        <input type="text" id="full-name" value="${escapeHtml(user?.full_name ?? '')}" required>
                    </div>
                    <div class="form-group">
                        <label for="profile-email">Email</label>
                        <input type="email" id="profile-email" value="${escapeHtml(user?.email ?? '')}" required>
                        <p class="form-hint">
                            This is also the address you sign in with, and where a password reset
                            is sent. Changing it needs your password, and the new address has to
                            be confirmed before it takes effect.
                        </p>
                    </div>
                    <div class="form-group">
                        <label for="profile-current-password">Current password</label>
                        <input type="password" id="profile-current-password" autocomplete="current-password">
                        <p class="form-hint">Only needed if you are changing your email address.</p>
                    </div>
                    <button type="submit" class="btn btn-primary">Save changes</button>
                </form>
                <div id="email-verify-panel"></div>
                <p class="text-muted">Member since ${escapeHtml(formatDate(user?.created_at))}</p>
            </section>

            <section class="form-section">
                <h2>Password</h2>
                <form id="account-password-form" class="account-form" novalidate>
                    <div class="form-group">
                        <label for="current-password">Current password</label>
                        <input type="password" id="current-password" autocomplete="current-password" required>
                    </div>
                    <div class="form-group">
                        <label for="new-password">New password</label>
                        <input type="password" id="new-password" autocomplete="new-password" required>
                        <p class="form-hint">At least 12 characters, with upper and lower case, a number and a symbol.</p>
                    </div>
                    <div class="form-group">
                        <label for="confirm-password">Confirm new password</label>
                        <input type="password" id="confirm-password" autocomplete="new-password" required>
                    </div>
                    <button type="submit" class="btn btn-primary">Change password</button>
                </form>
            </section>

            <section class="form-section">
                <h2>Two-factor sign-in</h2>
                <div id="mfa-panel"><p class="text-muted">Loading...</p></div>
            </section>

            <section class="form-section">
                <h2>Your information</h2>
                <p class="text-muted">
                    The <a href="/privacy" data-link>privacy policy</a> says what is stored and why.
                    To close your account, see <a href="/delete-account" data-link>deleting your account</a>.
                </p>
            </section>
        </div>`;

    bindProfileForm();
    bindPasswordForm();
    await renderMfaPanel();
}

function bindProfileForm() {
    document.getElementById('account-details-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = e.target.querySelector('button[type=submit]');
        btn.disabled = true;
        try {
            const fullName = document.getElementById('full-name').value.trim();
            const email = document.getElementById('profile-email').value.trim();
            const currentPassword = document.getElementById('profile-current-password').value;

            // The handler returns {success:true}, not the updated row, so refresh the
            // profile rather than assuming the response body is the new user.
            const result = await api.updateProfile({ email, fullName, currentPassword });
            document.getElementById('profile-current-password').value = '';
            await auth.loadUser();
            // The sidebar shows the name and initials, so it has to re-render.
            renderNavigation();

            if (result?.email_verification_required) {
                // The name is saved; the address is not, and will not be until the code
                // Cognito just sent is confirmed. Saying "Profile updated" here would be
                // a lie about the half that matters.
                renderEmailVerification(email);
            } else {
                showToast('Profile updated', 'success');
            }
        } catch (err) {
            showToast(err.message ?? 'Could not save your profile', 'error');
        } finally {
            btn.disabled = false;
        }
    });
}

/**
 * The confirmation step for an email change.
 *
 * Until the code is entered, the account keeps its previous address -- for sign-in and,
 * more to the point, for password resets. The copy says so plainly rather than implying
 * the change has already happened, because a user who believes their address has moved
 * and then cannot receive a reset has been actively misled.
 */
function renderEmailVerification(pendingEmail) {
    const panel = document.getElementById('email-verify-panel');
    if (!panel) return;

    panel.innerHTML = `
        <div class="setting-row">
            <p><strong>Confirm your new email address</strong></p>
            <p class="form-hint">
                We've sent a six-digit code to ${escapeHtml(pendingEmail)}. Until you enter it,
                your account keeps its previous address for signing in and password resets.
            </p>
            <form id="email-verify-form" class="account-form" novalidate>
                <div class="form-group">
                    <label for="email-code">Code from that email</label>
                    <input type="text" id="email-code" inputmode="numeric" maxlength="6"
                           autocomplete="one-time-code" required>
                </div>
                <button type="submit" class="btn btn-primary">Confirm new email</button>
            </form>
        </div>`;

    enhanceCodeInput(document.getElementById('email-code'));

    document.getElementById('email-verify-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = e.target.querySelector('button[type=submit]');
        btn.disabled = true;
        try {
            await api.verifyProfileEmail(document.getElementById('email-code').value.trim());
            panel.innerHTML = '';
            await auth.loadUser();
            renderNavigation();
            showToast('Email address confirmed', 'success');
        } catch (err) {
            showToast(err.message ?? 'That code was not accepted', 'error');
            btn.disabled = false;
        }
    });
}

function bindPasswordForm() {
    document.getElementById('account-password-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const current = document.getElementById('current-password').value;
        const next = document.getElementById('new-password').value;
        const confirm = document.getElementById('confirm-password').value;

        if (next !== confirm) {
            showToast('Those passwords do not match', 'error');
            return;
        }

        const btn = e.target.querySelector('button[type=submit]');
        btn.disabled = true;
        try {
            await api.changePassword(current, next);
            e.target.reset();
            showToast('Password changed', 'success');
        } catch (err) {
            showToast(err.message ?? 'Could not change your password', 'error');
        } finally {
            btn.disabled = false;
        }
    });
}

async function renderMfaPanel() {
    const panel = document.getElementById('mfa-panel');
    let status;
    try {
        status = await api.getMfaStatus();
    } catch (err) {
        panel.innerHTML = `<p class="form-error">Could not load two-factor status.</p>`;
        return;
    }

    if (status.totp_enabled) {
        panel.innerHTML = `
            <p><span class="badge badge-success">Enabled</span></p>
            <p class="text-muted">You'll be asked for a code from your authenticator app each time you sign in.</p>
            <button type="button" id="disable-mfa-btn" class="btn btn-danger">Turn off two-factor</button>
            <div id="disable-mfa-holder"></div>`;

        // A password field rather than a confirm() box: the server now requires the
        // current password, so a yes/no prompt could only ever be followed by a failure.
        document.getElementById('disable-mfa-btn').addEventListener('click', () => {
            const holder = document.getElementById('disable-mfa-holder');
            holder.innerHTML = `
                <form id="disable-mfa-form" class="account-form" novalidate>
                    <div class="form-group">
                        <label for="disable-mfa-password">Confirm your password</label>
                        <input type="password" id="disable-mfa-password"
                               autocomplete="current-password" required>
                        <p class="form-hint">
                            You'll sign in with just a password until you set two-factor up again.
                        </p>
                    </div>
                    <button type="submit" class="btn btn-danger">Turn off two-factor</button>
                    <button type="button" id="cancel-disable-mfa" class="btn btn-secondary">Cancel</button>
                </form>`;
            document.getElementById('disable-mfa-btn').disabled = true;
            document.getElementById('cancel-disable-mfa').addEventListener('click', renderMfaPanel);

            document.getElementById('disable-mfa-form').addEventListener('submit', async (e) => {
                e.preventDefault();
                const btn = e.target.querySelector('button[type=submit]');
                btn.disabled = true;
                try {
                    await api.disableMfa(document.getElementById('disable-mfa-password').value);
                    showToast('Two-factor turned off', 'success');
                    await renderMfaPanel();
                } catch (err) {
                    showToast(err.message ?? 'Could not turn off two-factor', 'error');
                    btn.disabled = false;
                }
            });
        });
        return;
    }

    panel.innerHTML = `
        <p><span class="badge badge-warning">Not set up</span></p>
        <p class="text-muted">
            Add a code from an authenticator app to your sign-in.
        </p>
        <button type="button" id="setup-mfa-btn" class="btn btn-primary">Set up two-factor</button>`;

    document.getElementById('setup-mfa-btn').addEventListener('click', startMfaSetup);
}

async function startMfaSetup() {
    const panel = document.getElementById('mfa-panel');
    panel.innerHTML = '<p class="text-muted">Preparing...</p>';

    let setup;
    try {
        setup = await api.setupMfa();
    } catch (err) {
        showToast(err.message ?? 'Could not start two-factor setup', 'error');
        await renderMfaPanel();
        return;
    }

    panel.innerHTML = `
        <p>Scan this with your authenticator app, then enter the 6-digit code it shows.</p>
        <canvas id="mfa-qr" aria-label="Two-factor setup QR code"></canvas>
        <p class="form-hint">Can't scan it? Enter this key manually:<br>
            <code>${escapeHtml(setup.secretCode)}</code>
        </p>
        <form id="mfa-verify-form" class="account-form" novalidate>
            <div class="form-group">
                <label for="mfa-code">Code from your app</label>
                <input type="text" id="mfa-code" inputmode="numeric" maxlength="6" autocomplete="one-time-code" required>
            </div>
            <button type="submit" class="btn btn-primary">Confirm</button>
            <button type="button" id="cancel-mfa-btn" class="btn btn-secondary">Cancel</button>
        </form>`;

    // Rendered client-side from the otpauth:// URI. v1 generated the QR on the server;
    // doing it here means the shared secret never needs a second round trip.
    try {
        await QRCode.toCanvas(document.getElementById('mfa-qr'), setup.otpauthUri, { width: 200, margin: 1 });
    } catch {
        // The manual key above is a complete fallback, so a QR failure isn't fatal.
        document.getElementById('mfa-qr')?.remove();
    }

    enhanceCodeInput(document.getElementById('mfa-code'));

    document.getElementById('cancel-mfa-btn').addEventListener('click', renderMfaPanel);

    document.getElementById('mfa-verify-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const code = document.getElementById('mfa-code').value.trim();
        const btn = e.target.querySelector('button[type=submit]');
        btn.disabled = true;
        try {
            await api.verifyMfaSetup(code);
            showToast('Two-factor is now on', 'success');
            await renderMfaPanel();
        } catch (err) {
            showToast(err.message ?? 'That code was not accepted', 'error');
            btn.disabled = false;
        }
    });
}
